import React, { useState } from "react";
import useRoom, { roomsApi } from "./useRoom";
import { useRunner } from "./parts";
import { shortDate } from "../record/format.mjs";
import "./rooms.css";

/*
 * THE ROOM ON THE CARDS (AddToRoom board, B and C): the pricing card says
 * whether its quote is the one on the room, and the agreement card shares the
 * draft and takes the PandaDoc link. Both read the same room as the panel.
 */

const same = (a, b) => (a || []).join("\n").trim() === (b || []).join("\n").trim();

/** Under "What we quoted". `terms` are the card's lines; `at` the quote's date. */
export function PricingRoomStrip({ company, terms }) {
  const { data } = useRoom(company && company.id);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  if (!company || !company.id || !data || !terms || !terms.length) return null;
  const item = data.room ? (data.sections.pricing || [])[0] : null;
  const cur = item && item.pricing && item.pricing.current;
  const add = () => run(() => roomsApi.add(company.id, { section: "pricing" }));

  if (cur && same(cur.lines, terms)) {
    return (
      <div className="rm rm-strip">
        <span>
          <b>On the room</b>
          {cur.at ? ` · the quote of ${shortDate(cur.at)}` : ""}
          {item.pricing.previous ? " · Revised, the earlier one folds under it" : ""}
        </span>
      </div>
    );
  }
  return (
    <div className="rm rm-strip">
      <span>
        {cur ? (
          <>
            <b>The room shows the {cur.at ? `${shortDate(cur.at)} ` : "earlier "}quote.</b> Updating puts this one on top with a Revised tag. The old one folds under it.
          </>
        ) : (
          <>
            <b>Not on {company.name || "their"}’s room.</b> Adds this quote to Pricing, as a ladder.
          </>
        )}
      </span>
      {err && <span className="rm-err">{err}</span>}
      <button type="button" className="rm-btn rm-btn--primary" disabled={busy} onClick={add}>
        {busy ? "Saving…" : cur ? "Update room" : "Add to room"}
      </button>
    </div>
  );
}

/** On the agreement card: share the generated draft; paste the PandaDoc link. */
export function AgreementRoomStrip({ company }) {
  const { data } = useRoom(company && company.id);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [warn, setWarn] = useState(null);
  const run = useRunner(setBusy, setErr);
  if (!company || !company.id || !data) return null;
  const items = data.room ? data.sections.agreement || [] : [];
  const draft = items.find((i) => (i.content || {}).kind === "draft");
  const pd = items.find((i) => (i.content || {}).kind === "pandadoc");

  const share = () =>
    run(async () => {
      const res = await roomsApi.add(company.id, { section: "agreement", kind: "draft" });
      const it = ((res.sections && res.sections.agreement) || []).find((i) => (i.content || {}).kind === "draft");
      const w = it && it.content && it.content.warnings;
      setWarn(w && w.length ? `${w.length} ${w.length === 1 ? "blank" : "blanks"} for them to complete: ${w.slice(0, 3).join("; ")}` : null);
    });
  const addPd = () =>
    run(async () => {
      await roomsApi.add(company.id, { section: "agreement", kind: "pandadoc", url: url.trim() });
      setUrl("");
    });

  return (
    <div className="rm" style={{ display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid #E4E2DA", paddingTop: 12 }}>
      <span className="rm-label">On {company.name || "their"}’s room</span>
      <div className="rm-strip">
        <span>
          {draft ? (
            <>
              <b>Draft v{draft.content.version || 1} is on the room</b> · {shortDate(draft.content.generated_at || draft.created_at)}
              {data.newer_draft ? " · the terms changed since" : ""}
            </>
          ) : (
            <>
              <b>Draft .docx</b> · generated from the agreement terms, for their legal team to mark up
            </>
          )}
        </span>
        <button type="button" className={`rm-btn${draft && !data.newer_draft ? "" : " rm-btn--primary"}`} disabled={busy} onClick={share}>
          {draft ? `Share v${(draft.content.version || 1) + 1} on room` : "Share draft on room"}
        </button>
      </div>
      {warn && <span className="rm-hint">{warn}</span>}
      {pd ? (
        <div className="rm-strip">
          <span>
            <b>Review &amp; sign in PandaDoc</b> is on the room{pd.content.sent_at ? ` · since ${shortDate(pd.content.sent_at)}` : ""}
            {pd.opened ? ` · opened ${shortDate(pd.opened.at)}` : " · not opened yet"}
          </span>
        </div>
      ) : (
        <div className="rm-field">
          <label htmlFor={`rm-pd-${company.id}`}>PandaDoc link</label>
          <div className="rm-pdrow">
            <input id={`rm-pd-${company.id}`} className="rm-input" placeholder="https://app.pandadoc.com/document/…" value={url} onChange={(e) => setUrl(e.target.value)} />
            <button type="button" className="rm-btn" disabled={busy || !url.trim()} onClick={addPd}>Add to room</button>
          </div>
          <span className="rm-hint">Shows as “Review &amp; sign in PandaDoc”. The draft download stays beside it until you remove it.</span>
        </div>
      )}
      {err && <span className="rm-err">{err}</span>}
    </div>
  );
}
